<template>
  <AppModal
    :open="paidFeatureStore.visible"
    :title="headlineText"
    :description="$t('app.paidFeatureSubhead')"
    test-id="paid-feature-modal"
    @close="paidFeatureStore.close()"
  >
    <div class="-mx-4 -mt-4">
      <div class="h-1 w-full bg-primary shrink-0" aria-hidden="true" />
      <div class="px-4 pt-4">
        <span
          class="inline-flex items-center rounded-full bg-primary/15 text-primary text-xs font-bold px-2.5 py-1"
          aria-hidden="true"
        >
          {{ planLabel }}
        </span>
        <p class="mt-3 text-base font-medium text-slate-700 dark:text-slate-200">
          {{ $t('app.paidFeatureSubhead') }}
        </p>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {{ descriptionText }}
        </p>
        <p class="mt-3 text-sm text-slate-700 dark:text-slate-300">
          {{ pricingLine }}
        </p>
        <p class="mt-4 text-sm font-semibold text-slate-800 dark:text-slate-100">
          {{ $t('app.paidFeatureWhatIncluded') }}
        </p>
        <ul class="mt-2 space-y-2" role="list">
          <li
            v-for="item in benefitItems"
            :key="item.key"
            class="flex items-center gap-3 rounded-lg bg-slate-50 dark:bg-zinc-800/50 px-3 py-2 text-sm text-slate-700 dark:text-slate-300"
          >
            <span class="flex h-8 w-8 shrink-0 items-center justify-center text-primary" aria-hidden="true">
              <span class="material-icons text-xl">{{ item.icon }}</span>
            </span>
            <span>{{ $t(item.labelKey) }}</span>
          </li>
        </ul>
      </div>
    </div>
    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <AppButton
          type="button"
          variant="secondary"
          class="min-h-[44px]"
          @click="paidFeatureStore.close()"
        >
          {{ $t('app.maybeLater') }}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          class="min-h-[44px]"
          data-testid="paid-feature-upgrade"
          @click="handleUpgrade"
        >
          {{ $t('app.upgrade') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePaidFeatureStore } from '@/stores/paidFeature'
import AppModal from '@/components/ui/AppModal.vue'
import AppButton from '@/components/ui/AppButton.vue'

const { t } = useI18n()
const router = useRouter()
const paidFeatureStore = usePaidFeatureStore()

const planLabel = computed(() => {
  const plan = paidFeatureStore.requiredPlan
  if (plan === 'growth') return 'Growth'
  if (plan === 'vip') return 'VIP'
  return 'Pro'
})

const headlineText = computed(() => {
  const plan = paidFeatureStore.requiredPlan
  if (plan === 'growth') return t('app.upgradeToGrowth')
  if (plan === 'vip') return t('app.upgradeToVip')
  return t('app.paidFeatureHeadline')
})

const pricingLine = computed(() => t('app.paidFeaturePricingLine'))

const descriptionText = computed(() => {
  const key = paidFeatureStore.featureKey
  if (key === 'announcement' || key === 'announcements') return t('app.paidFeatureAnnouncementDescription')
  if (key === 'mailing_list') return t('app.paidFeatureMailingListDescription')
  if (key === 'vip_site_fields') return t('app.paidFeatureSiteContentDescription')
  if (key === 'owner_orders') return t('app.paidFeatureOwnerOrdersDescription')
  return t('app.paidFeatureAiDescription')
})

const benefitItems = [
  { key: 'announcements', icon: 'campaign', labelKey: 'app.paidFeatureBenefitAnnouncements' },
  { key: 'tableReservation', icon: 'event_seat', labelKey: 'app.paidFeatureBenefitTableReservation' },
  { key: 'mailingList', icon: 'mail', labelKey: 'app.paidFeatureBenefitMailingList' },
  { key: 'ownerOrders', icon: 'receipt_long', labelKey: 'app.paidFeatureBenefitOwnerOrders' },
  { key: 'vipSiteFields', icon: 'tune', labelKey: 'app.paidFeatureBenefitSiteContent' },
  { key: 'noAds', icon: 'block', labelKey: 'app.paidFeatureBenefitNoAds' },
  { key: 'multipleRestaurants', icon: 'storefront', labelKey: 'app.paidFeatureBenefitMultipleRestaurants' },
]

function handleUpgrade() {
  paidFeatureStore.close()
  router.push({ name: 'Billing' })
}
</script>
